import { useApiAction } from "api/drops/useApiAction";
import { puzzelLogin, puzzelRefreshToken } from "api/puzzel/puzzel";
import {
  PUZZEL_ACCESS_TOKEN,
  PUZZEL_CUSTOMER_KEY,
} from "api/puzzel/puzzelHttpClient";
import { useFeatureFlag } from "app/FeatureFlags/useFeatureFlags";
import {
  checkPuzzelSession,
  redirectToPuzzelLogin,
} from "app/PuzzelAuth/puzzelAuth";
import {
  getPuzzelOrgConfig,
  PUZZEL_OIDC_LOGIN_FLAG,
  setPuzzelOidcEnabled,
} from "app/PuzzelAuth/puzzelAuthConfig";
import { log, LogLevel } from "logging/datadogBrowserLogs";
import React, {
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

type PuzzelAuthenticationValue = {
  isAuthenticated: boolean;
  login: () => void;
  phoneNumberPrefix: string;
  functionalNumberPrefix: string;
  countryCode: string;
  maxAttempts: number;
  secondsBetweenAttempts: number;
  ciqType: string;
  queueKey: string;
  maxOneRequestForPreferredAgent: boolean;
  preferredAgentWaitTimeSeconds: number;
  reservedAgentWaitTimeSeconds: number;
};

const noop = () => {};

const initialValue: PuzzelAuthenticationValue = {
  isAuthenticated: false,
  login: noop,
  ciqType: "",
  phoneNumberPrefix: "",
  functionalNumberPrefix: "",
  countryCode: "",
  maxAttempts: 0,
  maxOneRequestForPreferredAgent: false,
  preferredAgentWaitTimeSeconds: 0,
  queueKey: "",
  reservedAgentWaitTimeSeconds: 0,
  secondsBetweenAttempts: 0,
};

// Single shared context - CallControl/CallOut/Logon etc. all read from this regardless of
// which provider body (legacy/OIDC) is currently mounted below, so they never need to know
// which login mechanism is active.
const PuzzelAuthenticationContext = React.createContext(initialValue);

// --- Legacy: silent backend-mediated login, no user-facing login screen at all -------------
// TODO: remove this body (and the puzzelOidcLogin flag/branch) once OIDC has rolled out fully.

const fromPuzzelLoginResponse = (data: any): PuzzelAuthenticationValue => ({
  isAuthenticated: !!data.accessToken,
  login: noop,
  ciqType: data.ciqType,
  phoneNumberPrefix: data.phoneNumberPrefix,
  functionalNumberPrefix: data.functionalNumberPrefix,
  countryCode: data.countryCode,
  maxAttempts: data.maxAttempts,
  maxOneRequestForPreferredAgent: data.maxOneRequestForPreferredAgent,
  preferredAgentWaitTimeSeconds: data.preferredAgentWaitTimeSeconds,
  queueKey: data.queueKey,
  reservedAgentWaitTimeSeconds: data.reservedAgentWaitTimeSeconds,
  secondsBetweenAttempts: data.secondsBetweenAttempts,
});

const PuzzelAuthenticationProviderLegacy = (
  props: Record<string, unknown>,
): ReactElement => {
  const [puzzelContext, setPuzzelContext] =
    useState<PuzzelAuthenticationValue>(initialValue);
  const [loginResponse, performLogin] = useApiAction(puzzelLogin, false);
  const [refreshToken, setRefreshToken] = useApiAction(
    puzzelRefreshToken,
    false,
  );

  useEffect(() => {
    performLogin();
  }, []);

  useEffect(() => {
    let intervalId = 0;
    if (loginResponse.data !== null) {
      localStorage.setItem(
        PUZZEL_ACCESS_TOKEN,
        loginResponse.data?.accessToken,
      );
      localStorage.setItem(
        PUZZEL_CUSTOMER_KEY,
        loginResponse.data?.customerKey,
      );

      setPuzzelContext(() => fromPuzzelLoginResponse(loginResponse.data));

      intervalId = window.setInterval(() => {
        const { sessionId } = loginResponse.data;
        setRefreshToken({
          sessionId,
        });
      }, 1680000); // refresh token every 28 minutes
    }

    return () => {
      clearInterval(intervalId);
    };
  }, [loginResponse.data]);

  useEffect(() => {
    if (refreshToken.data !== null) {
      localStorage.setItem(PUZZEL_ACCESS_TOKEN, refreshToken.data?.accessToken);
    }
  }, [refreshToken.data]);

  return (
    <PuzzelAuthenticationContext.Provider value={puzzelContext} {...props} />
  );
};

// Re-validates in the background so a dead session doesn't keep showing as "logged in".
const SESSION_CHECK_INTERVAL_MS = 5 * 60 * 1000;

const PuzzelAuthenticationProviderOidc = (
  props: Record<string, unknown>,
): ReactElement => {
  const [puzzelContext, setPuzzelContext] =
    useState<PuzzelAuthenticationValue>(initialValue);

  // Non-redirecting check - the dashboard should work without Puzzel login, only the phone widget needs it.
  const checkAuthentication = () => {
    checkPuzzelSession()
      .then((isAuthenticated) => {
        setPuzzelContext(
          isAuthenticated
            ? {
                isAuthenticated: true,
                login: noop,
                ...getPuzzelOrgConfig(),
              }
            : initialValue,
        );
      })
      .catch((error) => {
        log(
          LogLevel.error,
          "PuzzelAuthenticationProvider",
          "Failed to check Puzzel session",
          error,
        );
      });
  };

  useEffect(() => {
    checkAuthentication();
    const intervalId = window.setInterval(
      checkAuthentication,
      SESSION_CHECK_INTERVAL_MS,
    );

    return () => {
      window.clearInterval(intervalId);
    };
  }, []);

  const login = useCallback(() => {
    void redirectToPuzzelLogin();
  }, []);

  const value = useMemo(
    () => ({ ...puzzelContext, login }),
    [puzzelContext, login],
  );

  return <PuzzelAuthenticationContext.Provider value={value} {...props} />;
};

const PuzzelAuthenticationProvider = (
  props: Record<string, unknown>,
): ReactElement => {
  const { enabled: oidcEnabled } = useFeatureFlag(PUZZEL_OIDC_LOGIN_FLAG);

  useEffect(() => {
    setPuzzelOidcEnabled(oidcEnabled);
  }, [oidcEnabled]);

  return oidcEnabled ? (
    <PuzzelAuthenticationProviderOidc {...props} />
  ) : (
    <PuzzelAuthenticationProviderLegacy {...props} />
  );
};

function usePuzzelAuthenticationContext(): PuzzelAuthenticationValue {
  const context = React.useContext(PuzzelAuthenticationContext);
  if (context === undefined) {
    throw new Error(
      "usePuzzelContext must be used within a PuzzelContextProvider",
    );
  }
  return context;
}

export { PuzzelAuthenticationProvider, usePuzzelAuthenticationContext };
